import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Eye, EyeOff, Loader2, RefreshCw } from "lucide-react";

// Clé de licence masquée par défaut : l'œil l'affiche, le bouton copie, et
// « Régénérer » (si fourni) remplace la clé après confirmation.
export const maskKey = (key = "") => (key ? `${key.slice(0, 3)}${key.slice(3).replace(/[A-Za-z0-9]/g, "•")}` : "");

export default function SecretKey({ value, onRegenerate, regenerating, compact = false, testid }) {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Clé copiée");
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copie impossible");
    }
  };

  const btn = "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/45 transition hover:bg-white/[0.08] hover:text-white disabled:opacity-40";
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center gap-1 rounded-xl border border-white/[0.07] bg-black/30 py-1 pl-3 pr-1">
        <code className={`min-w-0 flex-1 truncate font-mono ${compact ? "text-[11.5px]" : "text-xs"} ${shown ? "text-white/90" : "tracking-wider text-white/50"}`} data-testid={testid && `${testid}-value`}>
          {shown ? value : maskKey(value)}
        </code>
        <button type="button" onClick={() => setShown((v) => !v)} className={btn} title={shown ? "Masquer" : "Voir la clé"} aria-label={shown ? "Masquer la clé" : "Voir la clé"} data-testid={testid && `${testid}-toggle`}>
          {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
        <button type="button" onClick={copy} className={btn} title="Copier" aria-label="Copier la clé" data-testid={testid && `${testid}-copy`}>
          {copied ? <Check className="h-4 w-4 text-emerald-300" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
      {onRegenerate && (confirming ? (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2 text-[11.5px] text-amber-100/90">
          <span className="flex-1">L'ancienne clé ne marchera plus. Continuer ?</span>
          <button type="button" onClick={() => setConfirming(false)} className="rounded-md px-2 py-1 text-white/55 hover:bg-white/[0.06] hover:text-white">Annuler</button>
          <button
            type="button"
            disabled={regenerating}
            onClick={async () => { await onRegenerate(); setConfirming(false); setShown(true); }}
            className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-2.5 py-1 font-semibold text-black hover:bg-amber-400 disabled:opacity-50"
            data-testid={testid && `${testid}-confirm`}
          >
            {regenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}Régénérer
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setConfirming(true)} className="inline-flex items-center gap-1.5 text-[11.5px] text-white/45 transition hover:text-white" data-testid={testid && `${testid}-regenerate`}>
          <RefreshCw className="h-3.5 w-3.5" />Régénérer la clé (changer de serveur)
        </button>
      ))}
    </div>
  );
}
